import t from "./en";
import { getEnv } from "./env";
import { hasEntitlement, hasPermission } from "./selectors";

import {
  ManageSearch,
  Settings,
  ScreenSearchDesktop,
  MiscellaneousServices,
  Key,
  Album,
  AppRegistration,
  Build,
  SettingsApplications,
  ManageAccounts,
  Storage,
  GitHub,
  EventNote,
  BarChart,
  AttachMoney,
  AutoAwesome,
  Extension,
  Tune,
  PeopleAlt,
  People,
  History,
  Lock,
  Badge,
  AccountTree,
  Business,
  GroupWork,
  SupportAgent,
} from "@mui/icons-material";

export const API_URL = getEnv("REACT_APP_API_URL");

export const AUTH_API_URL = getEnv("REACT_APP_AUTH_API_URL") + "/auth";

export const USER_API_URL = getEnv("REACT_APP_AUTH_API_URL");

export const K8S_API_URL = getEnv("REACT_APP_K8S_API_URL");

export const CORE_API_URL =
  getEnv("REACT_APP_CORE_API_URL") || "https://core.control.apps.clue2.app";
export const BYTES = 1024;

export const TEST_ID = "TEST_ID";
export const INITIAL_STATE = { id: TEST_ID }; //used for drag and drop functionality
export const INDUSTRIES = [
  {
    id: "00000000-0000-0000-0000-000000100000",
    name: "Information technology (IT)",
  },
];
export const INPUT_TYPES = [
  { id: "1", name: "SINGLE_SELECT" },
  { id: "2", name: "MULTI_SELECT" },
  { id: "3", name: "SINGLE_LINE_TEXT" },
  { id: "4", name: "MULTI_LINE_TEXT" },
  { id: "5", name: "DATE" },
];

export const USER_SOURCE = [
  { id: "1", name: "LOCAL" },
  { id: "2", name: "GOOGLE" },
  { id: "3", name: "GITHUB" },
  { id: "4", name: "OTHER" },
];

export const USERS_ROLES = [
  { id: "1", name: "SYSTEM" },
  { id: "2", name: "ACCOUNT" },
];

export const USER_TYPE_SYSTEM = "SYSTEM";
export const USER_TYPE_ACCOUNT = "ACCOUNT";

export const USER_STATUS = [
  { id: "1", name: "Active", enabled: true },
  { id: "2", name: "Inactive", enabled: false },
];

export const NAV_ITEMS = (props) => [
  {
    id: "list1",
    label: `${t.manageApplications}`,
    to: "/",
    icon: ManageSearch,
    subItems: [
      {
        label: `${t.configsAndSecrets}`,
        to: "/config",
        SubIcon: Key,
      },
    ],
  },
  // Directory — canonical home for Accounts / Users / Groups / Roles.
  // Cross-tenant for SYSTEM operators, own-account for Account Admins.
  // Placed BEFORE Advanced because for most admins it's now the
  // primary daily surface (people-management > infra). The Accounts
  // subitem is SYSTEM-only; the Users label swaps to "Members" for
  // ACCOUNT_ADMIN callers via DIRECTORY_TAB_LABELS below.
  //
  // Gate SWITCHED FROM ROLE-NAME to ENTITLEMENT-BASED (PR-1.5f).
  //   • `directory.user.manage` / `directory.group.manage` / `member.view`
  //     are dot-notation keys emitted by `/api/me/permissions` from
  //     role_permissions ⋈ role_assignments — they survive role
  //     renames (SYSTEM_DIRECTORY_ADMIN → DIRECTORY_ADMIN, etc.).
  //   • `hasPermission(["SYSTEM"])` is retained as a wildcard fallback
  //     so platform super-users still see everything even if their
  //     snapshot didn't include the specific entitlement key.
  //
  // SCK-723 backward-compat: coord does NOT yet emit any directory.*
  // entitlement (V40 seeds only app/secret/project/member/billing/
  // platform keys; PermissionsService emits role-name strings like
  // "ACCOUNT_ADMIN"). Until the backend-side fix lands (seed +
  // PermissionsService wiring), fall back to accepting the role-name
  // strings coord ACTUALLY emits today — same pattern the Access
  // section uses below. Without this, every ACCOUNT_ADMIN /
  // SYSTEM_DIRECTORY_ADMIN caller sees no Directory nav.
  ...(hasEntitlement(props, [
    "directory.user.manage",
    "directory.group.manage",
    "member.view",
  ]) ||
  hasPermission(props, [
    "SYSTEM",
    "ACCOUNT_ADMIN",
    "SYSTEM_DIRECTORY_ADMIN",
    "SYSTEM_DIR_ADMIN",
    "DIRECTORY_ADMIN",
  ])
    ? [
        {
          id: "directory",
          label: "Directory",
          to: "/directory",
          icon: AccountTree,
          subItems: [
            // Accounts sub-tab is cross-tenant admin territory —
            // gated on the `directory.account.manage` entitlement
            // (SYSTEM wildcard fallback; SCK-723 backward-compat also
            // accepts SYSTEM_DIRECTORY_ADMIN role-name strings).
            ...(hasEntitlement(props, ["directory.account.manage"]) ||
            hasPermission(props, [
              "SYSTEM",
              "SYSTEM_DIRECTORY_ADMIN",
              "SYSTEM_DIR_ADMIN",
            ])
              ? [
                  {
                    label: "Accounts",
                    to: "/directory/accounts",
                    SubIcon: Business,
                  },
                ]
              : []),
            {
              label: props?.userType === "SYSTEM" ? "Users" : "Members",
              to: "/directory/users",
              SubIcon: People,
            },
            { label: "Groups", to: "/directory/groups", SubIcon: GroupWork },
            { label: "Roles", to: "/directory/roles", SubIcon: Badge },
          ],
        },
      ]
    : []),
  // Access — post-Directory-scaffold this section carries security-
  // policy tabs only: Support Audit (SCK-569) and Security · CMK
  // (SCK-631), with SSO still shipping as a preview shell.
  // Members / Invitations / Roles moved to Directory; the legacy
  // /access/members etc. redirects live in App.js.
  ...(hasPermission(props, ["SYSTEM", "ACCOUNT_ADMIN"])
    ? [
        {
          id: "access",
          label: "Access",
          to: "/access/support-audit",
          icon: PeopleAlt,
          subItems: [
            {
              label: "Support Audit",
              to: "/access/support-audit",
              SubIcon: History,
            },
            {
              label: "Security · CMK",
              to: "/access/security/cmk",
              SubIcon: Lock,
            },
          ],
        },
      ]
    : []),
  ...(hasPermission(props, ["SYSTEM", "ACCOUNT_ADMIN"])
    ? [
        {
          id: "list2",
          label: `${t.advanced}`,
          to: "/images",
          icon: ScreenSearchDesktop,
          subItems: [
            {
              label: `${t.images}`,
              to: "/images",
              SubIcon: Album,
            },
            {
              label: `${t.registries}`,
              to: "/registries",
              SubIcon: AppRegistration,
            },
            {
              label: `${t.gitRepos}`,
              to: "/git-secrets",
              SubIcon: GitHub,
            },
            {
              label: `${t.builds}`,
              to: "/builds",
              SubIcon: Build,
            },
            {
              label: `${t.servicesItem}`,
              to: "/services",
              SubIcon: SettingsApplications,
            },
            {
              label: t.events,
              to: "/logs",
              SubIcon: EventNote,
            },
          ],
        },
      ]
    : []),
  ...(hasPermission(props, ["SYSTEM"])
    ? [
        {
          id: "list3",
          label: `${t.managePlatform}`,
          to: "/service-accounts",
          icon: Settings,
          subItems: [
            {
              label: `${t.serviceAccounts}`,
              to: "/service-accounts",
              SubIcon: ManageAccounts,
            },
            // builders, cluster-stacks, cluster-stores,
            // kubernetes-clusters, domains nav entries removed —
            // SYSTEM-only surfaces moved to admin app
            // (clue2solve/aws-dashboard, Wave-1 migration).
            {
              label: `${t.manage} ${t.database}`,
              to: "/manage-db",
              SubIcon: Storage,
            },
            {
              label: t.platformLimits,
              to: "/platform-limits",
              SubIcon: Tune,
            },
          ],
        },
      ]
    : []),
    {
      id: "metrics",
      label: t.metrics,
      to: "/metrics",
      icon: BarChart,
    },
  // SCK-549 PR-8 — dedicated /support surface (bot + tickets).
  // Available to every authenticated caller; the floating widget on
  // every page and this page are the two entry points to the
  // Support Bot Phase 1 experience.
  {
    id: "support",
    label: "Support",
    to: "/support",
    icon: SupportAgent,
  },
  {
    id: "costs",
    label: "Costs",
    to: "/costs",
    icon: AttachMoney,
  },
  {
    id: "daari",
    label: "Daari",
    to: "/daari",
    icon: Extension,
    subItems: [
      {
        label: "Overview",
        to: "/daari",
        SubIcon: MiscellaneousServices,
      },
      {
        // LLM Gateway is a Daari provider (create-bind-verify +
        // C2A_SYSTEM_ENV contract); nested here to reflect the
        // platform model.
        label: "LLM Spend",
        to: "/llm/spend",
        SubIcon: AutoAwesome,
      },
    ],
  },
  // audit nav entry removed — SYSTEM-only surface moved to admin app
  // (clue2solve/aws-dashboard, Wave-1 migration).
];

// NAV_ITEMS_MANAGEMENT_ACCOUNT retired (SCK-638). The legacy account-
// section nav was mounted under a SECOND MainLayout wrapper in App.js
// whose only nav entry was "Account", which clobbered the primary
// NAV_ITEMS drawer whenever a viewer entered /directory or /access.
// The wrapper was collapsed into the single NAV_ITEMS-fed MainLayout,
// so this export has no callers left.

/**
 * Label variants for the Directory sub-nav. SYSTEM operators see
 * cross-tenant nouns ("Accounts", "Users"); ACCOUNT_ADMINs see the
 * account-local equivalents ("Members" instead of Users). SYSTEM
 * users also get an Accounts tab that ACCOUNT_ADMINs never see.
 */
export const DIRECTORY_TAB_LABELS = {
  system: {
    accounts: "Accounts",
    users: "Users",
    groups: "Groups",
    roles: "Roles",
  },
  account: {
    accounts: "Accounts", // never rendered — filtered out for ACCOUNT_ADMIN
    users: "Members",
    groups: "Groups",
    roles: "Roles",
  },
};

export const PAGE_SIZE = 100;

export const ROWS_PARAMS_LOGS = (data) => ({
  header: [t.name, t.type, t.operation, t.createdBy, t.createdOn],
  body: data,
  bodyCount: data,

  bodyParams: (val) => [
    { value: val?.displayName },
    { value: val?.objectType },
    { value: val?.operationType },
    {
      value: val?.createdBy?.familyName
        ? `${val?.createdBy?.familyName} ${val?.createdBy?.givenName}`
        : val?.createdBy?.username,
    },
    { value: val?.createdOn },
  ],
});

export const AUDIT_LOGS = "AUDIT_LOGS";
export const EDIT_ITEM = "EDIT_ITEM";
export const DELETE_ITEM = "DELETE_ITEM";
export const JSON_ITEM = "JSON_ITEM";
export const NEW_ITEM = "NEW_ITEM";
export const PRIVATE = "PRIVATE";
export const PRIVATE_REGISTRY = "PRIVATE_REGISTRY";
export const GIT_NEW_CREDENTIALS = "GIT_NEW_CREDENTIALS";
export const REGISTRY_NEW_CREDENTIALS = "REGISTRY_NEW_CREDENTIALS";
export const EDIT_VARIABLE = "EDIT_VARIABLE";
export const DELETE_VARIABLE = "DELETE_VARIABLE";
export const ADD_VARIABLE = "ADD_VARIABLE";
export const FORCE_BUILD = "FORCE_BUILD";
export const JSON_ADD_VARIABLES = "JSON_ADD_VARIABLES";
export const BUILD_LOGS = "BUILD_LOGS";

export const TYPE_DB = [{ id: "POSTGRES" }, { id: "MYSQL" }];
export const SOURCES_DB = [
  { id: "PLATFORM_HOSTED_DB_SERVER" },
  { id: "PLATFORM_HOSTED_CLUSTER" },
];

export const FALSE = "False";

export const STATUS_COLOR = [
  { status: "FALSE", color: "rgb(244, 67, 54)" },
  { status: "TRUE", color: "rgb(76, 175, 80)" },
  { status: "UNKNOWN", color: "#B76E00" },
];

export const ROLE_PERMISSIONS = {
  ACCOUNT_ADMIN: [
    "ACCOUNT_ADMIN",
    "addApplications",
    "editApplications",
    "addUsersAndGroups",
    "editUsersAndGroups",
  ],
  ACCOUNT_MANAGER: ["editUsersAndGroups"],
  SERVICE_ADMIN: ["CreateServiceInstance", "EditServiceInstance"],
  SERVICE_MANAGER: ["EditServiceInstance"],
  APPLICATION_ADMIN: ["addApplications", "editApplications"],
  APPLICATION_MANAGER: ["editApplications"],
  APPLICATION_DEVELOPER: ["readOnly"],
};

export const MAX_LIMITS = {
  applications: 5,
  secrets: 10,
  postgresInstances: 1,
  clusters: 0,
};
